export type CharRange = {
	start: number;
	end: number;
};

export type Token = {
	start: number;
	end: number;
	key: string;
};

export function tokenize(text: string): Token[] {
	const tokens: Token[] = [];
	const pattern = /\S+/g;
	let match: RegExpExecArray | null;
	while ((match = pattern.exec(text)) !== null) {
		const raw = match[0];
		const key = raw
			.normalize("NFKC")
			.replace(/[^\p{L}\p{N}]/gu, "")
			.toLowerCase();
		if (key === "") continue;
		tokens.push({ start: match.index, end: match.index + raw.length, key });
	}
	return tokens;
}

function buildShingleSet(tokens: Token[], n: number): Set<string> {
	const shingles = new Set<string>();
	for (let i = 0; i <= tokens.length - n; i++) {
		shingles.add(
			tokens
				.slice(i, i + n)
				.map((t) => t.key)
				.join(" "),
		);
	}
	return shingles;
}

function tokensMatchingShingles(tokens: Token[], otherShingles: Set<string>, n: number): CharRange[] {
	if (tokens.length < n) return [];

	const marked = new Array<boolean>(tokens.length).fill(false);
	for (let i = 0; i <= tokens.length - n; i++) {
		const shingle = tokens
			.slice(i, i + n)
			.map((t) => t.key)
			.join(" ");
		if (!otherShingles.has(shingle)) continue;
		for (let j = i; j < i + n; j++) marked[j] = true;
	}

	const runs: CharRange[] = [];
	let runStart: number | null = null;
	for (let i = 0; i < tokens.length; i++) {
		if (marked[i]) {
			if (runStart === null) runStart = tokens[i].start;
		} else if (runStart !== null) {
			runs.push({ start: runStart, end: tokens[i - 1].end });
			runStart = null;
		}
	}
	if (runStart !== null) runs.push({ start: runStart, end: tokens[tokens.length - 1].end });

	return runs;
}

export function findShingleOverlapRuns(
	subjectAnswer: string,
	partnerAnswer: string,
	n = 3,
): { subjectRuns: CharRange[]; partnerRuns: CharRange[] } {
	const subjectTokens = tokenize(subjectAnswer);
	const partnerTokens = tokenize(partnerAnswer);

	const subjectShingles = buildShingleSet(subjectTokens, n);
	const partnerShingles = buildShingleSet(partnerTokens, n);

	return {
		subjectRuns: tokensMatchingShingles(subjectTokens, partnerShingles, n),
		partnerRuns: tokensMatchingShingles(partnerTokens, subjectShingles, n),
	};
}
